<template>
  <div class="bg-surface-level-100 dark:bg-surface-level-800 h-full relative w-full">
    <img
      v-if="staticMapUrl"
      :src="staticMapUrl"
      :alt="
        address
          ? $t('commerce.storeLocationMap.addressImageAlt', { address })
          : $t('commerce.storeLocationMap.fallbackImageAlt')
      "
      class="duration-150 h-full object-cover transition-opacity w-full"
      :class="imageLoaded ? 'opacity-100' : 'opacity-0'"
      loading="lazy"
      decoding="async"
      @load="handleImageLoad"
      @error="onImageError"
    />

    <div
      v-if="!staticMapUrl || (!imageLoaded && !hasError)"
      class="absolute flex inset-0 items-center justify-center"
    >
      <div
        v-if="staticMapUrl && !hasError"
        class="animate-spin border-2 border-primary border-t-border-clear h-8 rounded-full w-8"
      />

      <MapPinIcon
        v-else
        class="dark:text-content-level-600 h-10 text-content-level-300 w-10"
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { MapPinIcon } from '@heroicons/vue/24/outline'
import { geocodeAddress } from '~/composables/geocodeAddress'
import { getProviderConfig } from '~/composables/getProviderConfig'
import { resolveMapProvider } from '~/lib/geo-providers/resolveMapProvider'
import type { MapProvider } from '~/lib/geo-providers/interfaces'
import { hasText } from '~/utils/hasText'
import { useLogger } from '~/composables/useLogger'

const props = withDefaults(defineProps<Props>(), {
  active: false,
  address: '',
  latitude: null,
  longitude: null,
  markerColor: 'ea580c',
  zoom: 14
})

const logger = useLogger('StoreLocationMap')

interface Props {
  /** Latitude for the map center. */
  latitude?: number | null
  /** Longitude for the map center. */
  longitude?: number | null
  /** Address used to resolve missing coordinates. */
  address?: string
  /** When true, the map should resolve coordinates (e.g. modal open) */
  active?: boolean
  /** Marker color (hex without #) */
  markerColor?: string
  /** Map zoom level */
  zoom?: number
}

const colorMode = useColorMode()
const mapStyle = computed(() => (colorMode.value === 'dark' ? 'dark-v11' : 'streets-v12'))

const resolvedCoordinates = ref<[lng: number, lat: number] | null>(null)
// Resolved via resolveMapProvider() so the static-map URL tracks the selected provider.
const mapProvider = ref<MapProvider | null>(null)
const mapAccessToken = ref<string>('')
const imageLoaded = ref(false)
const handleImageLoad = () => {
  imageLoaded.value = true
}
const hasError = ref(false)

function hasValidCoordinates(lat?: number | null, lng?: number | null) {
  return (
    lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
  )
}

const staticMapUrl = computed(() => {
  if (resolvedCoordinates.value === null || mapProvider.value === null || !mapAccessToken.value) {
    return null
  }

  const [lng, lat] = resolvedCoordinates.value
  return mapProvider.value.buildStaticMapUrl({
    accessToken: mapAccessToken.value,
    lat,
    lng,
    mapboxStyle: mapStyle.value,
    markerColor: props.markerColor,
    zoom: props.zoom
  })
})

function onImageError() {
  hasError.value = true
  imageLoaded.value = false
}

async function resolveCoordinates() {
  imageLoaded.value = false
  hasError.value = false

  const { provider } = getProviderConfig()
  mapAccessToken.value = provider.accessToken
  mapProvider.value = provider.accessToken ? await resolveMapProvider() : null

  if (hasValidCoordinates(props.latitude, props.longitude)) {
    resolvedCoordinates.value = [props.longitude!, props.latitude!]
    return
  }

  if (!hasText(props.address) || props.address.trim().length === 0) {
    resolvedCoordinates.value = null
    return
  }

  const outcome = await geocodeAddress(props.address)
  resolvedCoordinates.value = outcome.status === 'resolved' ? [outcome.lng, outcome.lat] : null
}

watch(
  () => [props.latitude, props.longitude, props.address, props.active] as const,
  ([, , , active]) => {
    if (active) {
      resolveCoordinates().catch((error: unknown) => {
        logger.error('resolveCoordinates', { error })
      })
    }
  },
  { immediate: true }
)
</script>
